import { atom, selector } from 'recoil'
import _ from 'lodash'
import {
  AMDate,
  AMDayValue,
  AMMobilityAggregatedMethod,
  PatternsWeekdayDatesResponse,
  PatternsWeekdayResponse,
  Polygon,
  QuarterPeriod,
  amMobilityAggregatedMethodEnum,
} from 'src/common-services'
import { alphamapClient, footfallClient } from 'src/clients'
import { DEFAULT_LRU_SIZE, keyFactory, retryAtom, uncachedSelector } from 'src/utils/recoil'
import { venueMatch } from 'src/atoms/search'
import { ifNotFound } from 'src/utils/ajax'
import { areaVisitsValidEndDate, multipolygon } from 'src/components/insights/common/visits/atoms/area'
import { DropdownOption } from 'src/components/dropdown'

const key = keyFactory('insights-footfall')

export const showPatternsWeekdayDates = atom<boolean>({
  key: key('show-patterns-weekday-dates'),
  default: true,
})

export const patternsWeekdayDates = selector<PatternsWeekdayDatesResponse>({
  key: key('patterns-weekday-dates'),
  get: ({ get }) => {
    get(retryAtom)
    return footfallClient.getPatternsWeekdayDates()
  },
})

export const patternsWeekdayDatesOptions = selector<DropdownOption[]>({
  key: key('patterns-weekday-dates-options'),
  get: ({ get }) => {
    const patternsWeekdayDatesValue = get(patternsWeekdayDates)
    return patternsWeekdayDatesValue.periods.map((period, index) => ({
      id: index.toString(),
      label: period.quarter,
    }))
  },
})

export const patternsWeekdayInternalSelectedDateIndex = atom<number | undefined>({
  key: key('patterns-weekday-internal-selected-date-index'),
  default: undefined,
})

export const patternsWeekdaySelectedDateIndex = selector<number>({
  key: key('patterns-weekday-selected-date-index'),
  get: ({ get }) => {
    const patternsWeekdayDatesValue = get(patternsWeekdayDates)
    const patternsWeekdayInternalSelectedDateIndexValue = get(patternsWeekdayInternalSelectedDateIndex)

    if (
      patternsWeekdayInternalSelectedDateIndexValue !== undefined &&
      patternsWeekdayDatesValue.periods[patternsWeekdayInternalSelectedDateIndexValue]
    ) {
      return patternsWeekdayInternalSelectedDateIndexValue
    }
    return patternsWeekdayDatesValue.periods.length - 1
  },
})

export const patternsWeekdaySelectedDate = selector<QuarterPeriod>({
  key: key('patterns-weekday-selected-date'),
  get: ({ get }) => {
    const patternsWeekdayDatesValue = get(patternsWeekdayDates)
    const patternsWeekdaySelectedDateIndexValue = get(patternsWeekdaySelectedDateIndex)
    return patternsWeekdayDatesValue.periods[patternsWeekdaySelectedDateIndexValue]
  },
})

export const venuePatternsDayDate = atom<AMDate>({
  key: key('venue-patterns-day-date'),
  default: areaVisitsValidEndDate,
})

const venuePatternsWeekday = uncachedSelector<PatternsWeekdayResponse | undefined>({
  key: key('venue-patterns-weekday'),
  get: ({ get }) => {
    const date = get(venuePatternsDayDate)
    const venue = get(venueMatch)
    if (!venue) {
      return undefined
    }

    get(retryAtom)

    return ifNotFound(
      footfallClient.getPatternsWeekday({
        start: date,
        venueId: venue.venueId,
      }),
      undefined
    )
  },
  lru: DEFAULT_LRU_SIZE,
})

export const areaDailyAggregationMethod = atom<AMMobilityAggregatedMethod>({
  key: key('area-daily-aggregation-method'),
  default: amMobilityAggregatedMethodEnum.avg,
})

export const areaPatternsWeekday = uncachedSelector<AMDayValue[] | undefined>({
  key: key('area-patterns-weekday'),
  get: ({ get }) => {
    const date = get(venuePatternsDayDate)
    get(retryAtom)

    const shape = get(multipolygon)
    const polygon: Polygon = {
      type: 'Polygon',
      coordinates: shape.coordinates[0],
    }
    const method = get(areaDailyAggregationMethod)
    return ifNotFound(
      alphamapClient.mobility.getPolygonVisitsByDay({
        date,
        polygon,
        method,
      }),
      undefined
    )
  },
  lru: DEFAULT_LRU_SIZE,
})

export const averageVisitsPerWeek = selector<number | undefined>({
  key: key('average-visits-per-week'),
  get: ({ get }) => {
    const data = get(areaPatternsWeekday)
    if (!data?.length) {
      return undefined
    }

    const mean = _.mean(data?.map((d) => d.value))
    return Math.round(mean)
  },
})

export const totalVisitsPerWeek = selector<number | undefined>({
  key: key('total-visits-per-week'),
  get: ({ get }) => {
    const data = get(areaPatternsWeekday)
    if (!data?.length) {
      return undefined
    }

    return _.sum(data?.map((d) => d.value))
  },
})

export const patternsWeekday = selector<PatternsWeekdayResponse | undefined>({
  key: key('patterns-weekday'),
  get: ({ get }) => {
    return get(venuePatternsWeekday)
  },
})
